import { FormTableCellDirtyProvider } from "@/contexts/FormTableCellDirtyContext";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadonlyObject } from "@/types/readOnly";
import { memo, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "../../data-table";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import { PageCard } from "@/components/PageCard";
import Spinner from "@/components/Spinner";
import { CardContent } from "@/components/ui/card";

interface QueryTableContentProps extends PropsWithChildren {
  onResize?: (size: DOMRectReadOnly) => void;
  onScroll?: (left: number, top: number) => void;
  className?: string;
  footer?: React.ReactNode;
  isInDialog?: boolean;
}

export const QueryTableContent = memo(
  <T extends ReadonlyObject, Q extends SuccessFetchListResponse<T>>({
    children,
    onResize,
    onScroll,
    className,
    footer,
    isInDialog,
  }: QueryTableContentProps) => {
    const { t } = useTranslation();
    const { features } = useDataTableSettings<T>();
    const { query } = useDataTableQuery<Q>();

    const innerContent = (
      <>
        <div className="flex min-h-0 flex-1 flex-col gap-2 border">
          {query.isLoading ? (
            <div className="flex min-h-[max(16rem,35vh)] flex-1 flex-col items-center justify-center">
              <Spinner label={t("table.loading")} />
            </div>
          ) : (
            <DataTable
              className="min-h-0 flex-1"
              getRowProps={features.getRowProps}
              onResize={onResize}
              onScroll={onScroll}
              footer={footer}
              isInDialog={isInDialog}
            />
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2">{children}</div>
      </>
    );

    return (
      <PageCard className={className}>
        <CardContent className="flex flex-col gap-2 p-2 justify-between grow h-full min-w-0">
          {features.editionHighlight ? (
            <FormTableCellDirtyProvider
              baseline={features.editionHighlight.baseline}
              recordKey={features.editionHighlight.recordKey}
            >
              {innerContent}
            </FormTableCellDirtyProvider>
          ) : (
            innerContent
          )}
        </CardContent>
      </PageCard>
    );
  },
);
